'use client';

import { createContext, useContext, useEffect, useMemo, useState } from 'react';

/**
 * "Has this tool produced a result yet?", shared between the tool client and the
 * CTA band further down the page.
 *
 * Why this exists: once the result card renders, it carries the page's single
 * conversion ask. The CTA band below it used to ask the same thing again, in a
 * different visual language, with a link that dropped the produced file. Rather
 * than delete the band (its related-tools grid is the page's internal-link
 * graph), the ask half hides while a result is on screen.
 *
 * It is a client context on purpose: `free-tool-page-shell` stays a Server
 * Component, so the SEO body and related tools keep rendering statically. A
 * client provider receiving server-rendered `children` passes them straight
 * through as an already-rendered payload.
 */

interface ResultState {
  hasResult: boolean;
  setHasResult: (value: boolean) => void;
}

const FreeToolResultContext = createContext<ResultState | null>(null);

export function FreeToolResultStateProvider({ children }: { children: React.ReactNode }) {
  const [hasResult, setHasResult] = useState(false);
  const value = useMemo(() => ({ hasResult, setHasResult }), [hasResult]);

  return (
    <FreeToolResultContext.Provider value={value}>{children}</FreeToolResultContext.Provider>
  );
}

/** Call from the result surface. No-ops when rendered outside a provider. */
export function useReportFreeToolResult(hasResult: boolean): void {
  const ctx = useContext(FreeToolResultContext);
  useEffect(() => {
    ctx?.setHasResult(hasResult);
  }, [ctx, hasResult]);
}

export function useHasFreeToolResult(): boolean {
  return useContext(FreeToolResultContext)?.hasResult ?? false;
}

/**
 * Renders its children only while no result is on screen. Wraps the CTA band's
 * ask so the band's related-tools grid (rendered by its Server Component parent)
 * is unaffected.
 */
export function HideWhenResult({ children }: { children: React.ReactNode }) {
  return useHasFreeToolResult() ? null : <>{children}</>;
}
